/**
 * src/admin-ui/src/components/AdminRoute.tsx
 *
 * Administrator route guard.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { useAuthStore } from '../store/authStore';
import { isAdmin } from '../utils/roles';
import { FullScreenState } from './utility.styles';

interface AdminRouteProps {
  children: React.ReactNode;
}

/**
 * Route guard that only renders children for authenticated admin users.
 * Unauthenticated users are sent to /login; authenticated non-admins to
 * /dashboard.
 */
const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuthStore();
  const location = useLocation();

  // Dev mode: bypass all checks when the flag is on.
  if (process.env.REACT_APP_DISABLE_AUTH === 'true') {
    return <>{children}</>;
  }

  if (loading) {
    return (
      <FullScreenState>
        <CircularProgress size={60} />
      </FullScreenState>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isAdmin(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default AdminRoute;
